/* ScheduleDaySlider.functions.js */
import React, { useContext, useEffect, useState } from 'react'
import { EmpresasContext } from '../../../contexts/EmpresasContext'
import { AuthContext } from '../../../contexts/AuthContext'
import { Services } from '../../../services'
import { getFutureDates } from '../../../utils'
import { AgendamentoContext } from '../../../contexts/AgendamentoContext'
import moment from 'moment'

export const useScheduleDaySlider = (cursorRef, centerDateRef) => {
  const { sttAuthCtx } = React.useContext(AuthContext)
  const { sttPg, actionsCtxEmps, loads } = useContext(EmpresasContext)
  const { sttCtxAgend } = useContext(AgendamentoContext)
  const reagendamento = sttCtxAgend.reagendamento
  // —————————————————————————————————————————————————————————
  // 1) Só na montagem: se nunca buscamos hora do servidor, chamamos API.
  //    Caso o dispositivo esteja mais de 15 min adiantado/atrasado,
  //    guardamos serverTime + fetchedAt. Senão, marcamos isValid=true
  // —————————————————————————————————————————————————————————
  useEffect(() => {
    if (!sttPg.validData.fetchedAt) {
      actionsCtxEmps.ValidarDataDispositivo()
    }
    // note: sem dependências, roda apenas uma vez (montagem)
  }, [])

  // —————————————————————————————————————————————————————————
  // 2) Cálculo de `currentDay`:
  //    - Se ainda não veio fetchedAt, usamos `moment()` (device) até a resposta chegar.
  //    - Se isValid===false, estimamos “horário do servidor” = serverTime + (agoraLocal - fetchedAt).
  //    - Se isValid===true, usamos `moment()` (device).
  // —————————————————————————————————————————————————————————
  let currentDay
  const { isValid, serverTime, fetchedAt } = sttPg.validData

  if (!fetchedAt) {
    // antes da primeira fetch terminar, seguimos com o horário local
    currentDay = moment()
  } else if (!isValid && serverTime) {
    // dispositivo estava fora de sincronia → estimamos “horário do servidor”
    const elapsedMs = moment().diff(fetchedAt)
    currentDay = moment(serverTime).add(elapsedMs, 'milliseconds')
  } else {
    // isValid === true → dispositivo em sincronia → usamos horário local
    currentDay = moment()
  }

  // —————————————————————————————————————————————————————————
  // 3) Configuração do slider, usando `currentDay` em vez de `moment()`
  // —————————————————————————————————————————————————————————
  const totalDaysPerScreen =
    sttAuthCtx.width < 768
      ? parseInt((sttAuthCtx.width - 40 * 2) / (35 + 3))
      : parseInt(((sttAuthCtx.width < 1200 ? sttAuthCtx.width - 28 : 1170) - 41 * 2) / 85 - 1)
  const rangeDays = 8

  const [sttCmp, setSttCmp] = useState({
    offset: 0,
    disabledPrevious: false,
    arrayDias: Services.OrganizarDias(currentDay.format('YYYY-MM-DD'), rangeDays),
    dataCenter: sttPg.selectedDay,
  })

  // —————————————————————————————————————————————————————————
  // 4) selectDay: atualiza `selectedDay` e “centraliza” no slider
  // —————————————————————————————————————————————————————————
  function selectDay(day) {
    if (loads.gradeHorarios) return
    if (sttPg.selectedDay.isSame(day, 'day')) return
    if (verificaDesativarGrade(day) || verificaMaximoDias(day)) return
    actionsCtxEmps.handleSttPg('selectedDay', day)
    setSttCmp(prev => ({ ...prev, dataCenter: day }))
    actionsCtxEmps.clearSelectedHorarios()
  }

  // —————————————————————————————————————————————————————————
  // 5) handleNext e handlePrevious (mesma lógica, mas usando `currentDay`)
  // —————————————————————————————————————————————————————————
  function handleNext() {
    const dataIndex = sttCmp.arrayDias.findIndex(dia => dia.isSame(sttCmp.dataCenter, 'day'))
    if (dataIndex === -1) return

    const nextDataIndex = dataIndex + totalDaysPerScreen
    if (nextDataIndex < sttCmp.arrayDias.length) {
      setSttCmp(prev => ({
        ...prev,
        dataCenter: prev.dataCenter.clone().add(totalDaysPerScreen, 'days'),
      }))
    } else {
      setSttCmp(prev => ({
        ...prev,
        offset: prev.offset + 1,
        arrayDias: getFutureDates(prev.arrayDias),
        dataCenter: prev.dataCenter.clone().add(totalDaysPerScreen, 'days'),
      }))
    }
  }

  function handlePrevious() {
    const prevCandidate = sttCmp.dataCenter.clone().subtract(totalDaysPerScreen, 'days')
    if (prevCandidate.isBefore(currentDay, 'day')) {
      setSttCmp(prev => ({
        ...prev,
        dataCenter: currentDay.clone(),
        disabledPrevious: true,
      }))
    } else {
      setSttCmp(prev => ({
        ...prev,
        dataCenter: prev.dataCenter.clone().subtract(totalDaysPerScreen, 'days'),
      }))
    }
  }

  // —————————————————————————————————————————————————————————
  // 6) getMonthHeader mantém a mesma lógica de antes
  // —————————————————————————————————————————————————————————
  function getMonthHeader() {
    const firstItemScreen = sttCmp.dataCenter
      .clone()
      .subtract(Math.floor(totalDaysPerScreen / 2), 'days')
    const lastItemScreen = sttCmp.dataCenter.clone().add(Math.floor(totalDaysPerScreen / 2), 'days')

    const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1)

    if (
      sttCmp.dataCenter.isSame(firstItemScreen, 'month') &&
      sttCmp.dataCenter.isSame(lastItemScreen, 'month')
    ) {
      return `${capitalize(sttCmp.dataCenter.format('MMMM'))} ${sttCmp.dataCenter.format('YYYY')}`
    } else if (
      sttCmp.dataCenter.isSame(firstItemScreen, 'month') &&
      !sttCmp.dataCenter.isSame(lastItemScreen, 'month')
    ) {
      return `${capitalize(sttCmp.dataCenter.format('MMMM'))}/${capitalize(lastItemScreen.format('MMMM'))} ${
        sttCmp.dataCenter.isSame(lastItemScreen, 'year')
          ? sttCmp.dataCenter.format('YYYY')
          : `${sttCmp.dataCenter.format('YYYY')}/${lastItemScreen.format('YY')}`
      }`
    } else if (
      !sttCmp.dataCenter.isSame(firstItemScreen, 'month') &&
      sttCmp.dataCenter.isSame(lastItemScreen, 'month')
    ) {
      return `${capitalize(firstItemScreen.format('MMMM'))}/${capitalize(sttCmp.dataCenter.format('MMMM'))} ${
        firstItemScreen.isSame(sttCmp.dataCenter, 'year')
          ? firstItemScreen.format('YYYY')
          : `${firstItemScreen.format('YYYY')}/${sttCmp.dataCenter.format('YYYY')}`
      }`
    }
  }

  // —————————————————————————————————————————————————————————
  // 7) verificaDesativarGrade e verificaMaximoDias usando `currentDay`
  // —————————————————————————————————————————————————————————
  function verificaDesativarGrade(dia) {
    if (sttAuthCtx.isMobile && sttPg.selectedQuadra?.desativaGrades.length) {
      for (let item of sttPg.selectedQuadra.desativaGrades) {
        if (dia.isBetween(moment(item.dataInicio), moment(item.dataFim), 'days', '[]')) {
          return true
        }
      }
    }
    if (sttAuthCtx.isMobile && Object.keys(reagendamento).length > 0) {
      const selectedDayStart = dia.clone().startOf('day')
      const baseDate =
        sttCtxAgend.pedido?.reagendamentos?.length === 0
          ? moment(sttCtxAgend.pedido.dataInicio).startOf('day')
          : moment(sttCtxAgend.pedido.reagendamentos[0].dataInicio).startOf('day')

      const diffReagend = selectedDayStart.diff(baseDate, 'days')
      if (diffReagend > 30) return true
    }
    return false
  }

  function verificaMaximoDias(dia) {
    if (sttAuthCtx.isMobile && sttPg.selectedQuadra?.regraAgendamento.maximoTempoAgendamento > 0) {
      const diff = dia.clone().startOf('day').diff(currentDay.clone().startOf('day'), 'days')
      if (diff >= sttPg.selectedQuadra.regraAgendamento.maximoTempoAgendamento) {
        return true
      }
    }
    return false
  }

  // —————————————————————————————————————————————————————————
  // 8) Observer para “infinite scroll” (igual antes)
  // —————————————————————————————————————————————————————————
  useEffect(() => {
    if (sttCmp.offset === 0) {
      getFutureDates(sttCmp.arrayDias)
    }
    const lastElement = document.querySelector('#lastItem')
    if (lastElement) {
      const intersectionObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setSttCmp(prev => ({
            ...prev,
            offset: prev.offset + 1,
            arrayDias: getFutureDates(prev.arrayDias),
          }))
        }
      })
      intersectionObserver.observe(lastElement)
      return () => intersectionObserver.disconnect()
    }
  }, [sttPg.empresa.idempresa, sttCmp.offset])

  // —————————————————————————————————————————————————————————
  // 9) Observer para botão “Previous” (igual antes)
  // —————————————————————————————————————————————————————————
  useEffect(() => {
    const firstElement = document.querySelector('#firstItem')
    if (firstElement) {
      const intersectionObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setSttCmp(prev => ({ ...prev, disabledPrevious: true }))
        } else {
          setSttCmp(prev => ({ ...prev, disabledPrevious: false }))
        }
      })
      intersectionObserver.observe(firstElement)
      return () => intersectionObserver.disconnect()
    }
  }, [sttCmp.arrayDias, sttCmp.dataCenter])

  // —————————————————————————————————————————————————————————
  // 10) Scroll automático para o `centerDateRef` (igual antes)
  // —————————————————————————————————————————————————————————
  useEffect(() => {
    if (centerDateRef.current) {
      centerDateRef.current.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      })
    }
  }, [sttCmp.dataCenter, loads.gradeHorarios])

  return {
    sttCmp,
    selectDay,
    handleNext,
    handlePrevious,
    getMonthHeader,
    verificaDesativarGrade,
    verificaMaximoDias,
  }
}
